/**
 * CELL-CURRENCY-LOCALE-1 (s208, D-206.18) — the ONE formatter for a planner CURRENCY CELL.
 *
 * ## Why it lives in `shared/` (s209)
 *
 * It was written at s208 in `client/src/lib/estate-format.ts`, which was the right home while the
 * only consumer was a browser control. It is not any more: `shared/cell-text.ts` — the one answer to
 * "given this stored cell, what text does a person read?" — is reachable from SERVER document
 * builders, and a currency cell that formats on screen and prints `8000` in a PDF is the same defect
 * in two halves. `shared/` is the only directory both sides can import, so the function moved here
 * whole and `client/src/lib/estate-format.ts` re-exports it. 🔴 It MOVED; it was not copied. Two
 * formatters is exactly how the locale bug this comment describes shipped in the first place.
 *
 * ## What was wrong at s208
 *
 * The six-line helper below existed BYTE-IDENTICALLY in `EditableCell.tsx` and
 * `GenericCategoryPage.tsx`, and both declared **`en-US` / `USD`** while every estate figure beside
 * them declared `en-CA` / `CAD`. A Canadian estate product was rendering a planner's bank balance in
 * US dollars — invisibly, because both locales print a bare `$`.
 *
 * 🔴 The invisibility is the reason this is worth writing down. Nothing looked wrong and nothing
 * would look wrong until the product renders in French: `fr-CA` with `USD` gives `1 234,50 $ US`, an
 * explicit US marker on a Canadian estate. For a product with an active jurisdiction seam that is a
 * dated risk, not a hypothetical.
 *
 * ## The ruling this makes, stated rather than made silently
 *
 * 🔴 Planner cells keep **2 decimal places**; estate figures keep **0**. That difference comes from
 * `maximumFractionDigits`, not from the locale, so unifying the locale must not quietly round the
 * cells. It is deliberate: a planner typing an exact balance should see their cents back, while a
 * DERIVED estate total is approximate by construction (see `formatApproxCAD`, which stays in
 * `estate-format.ts` with the other estate-surface helpers). Unifying the two would either invent
 * precision upstream or destroy it downstream.
 *
 * ## Stated limits — the `shared/phone.ts` convention
 *
 * - It **validates nothing**. A value with no digits at all is returned UNCHANGED, so a cell holding
 *   "unknown" or "" shows what the planner typed rather than `NaN` or an empty cell.
 * - 🔴 SHARP EDGE, pre-existing and pinned rather than fixed: it strips `[^0-9.-]` instead of
 *   parsing, so MIXED text keeps only its digits — `"about 5k"` renders as `$5.00`. That is wrong
 *   and it was not changed here, because s208's member is the LOCALE and quietly altering what a
 *   cell displays under cover of a locale fix is how a defect gets found six sessions later. The
 *   behaviour is pinned in `estate-format.test.ts` so it is a decision, not a surprise.
 * - It formats a MAGNITUDE and renders a negative in accounting style, `($1,234.50)`.
 * - It is for DISPLAY only. It is never the value written to `generic_entries`, and parsing its
 *   output back into a number is not supported — the grouping separators are locale-dependent.
 */
const cadCents = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

export function formatPlannerCurrency(raw: string): string {
  const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
  if (isNaN(num)) return raw;
  const fmt = cadCents.format(Math.abs(num));
  return num < 0 ? `(${fmt})` : fmt;
}

/**
 * s232 D-232.79 (3) — a planner's money as the EXECUTOR reads it: "$100,000", cents only when they are not zero
 * ("$442.50"). The typed tables store an amount in its column's own form ("100000.00", D-229.111(2)), so a drawer
 * that printed the stored text showed "100000.00" where the planner had typed "100000". Same parse and the same
 * limits as `formatPlannerCurrency` above (a value with no digits comes back unchanged; negatives in accounting
 * style); it differs ONLY in dropping zero cents — the planner's own cell keeps its two places (the s208 ruling).
 */
const cadWhole = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
export function formatMoneyToRead(raw: string): string {
  const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
  if (isNaN(num)) return raw;
  const abs = Math.abs(num);
  const fmt = Math.round(abs * 100) % 100 === 0 ? cadWhole.format(abs) : cadCents.format(abs);
  return num < 0 ? `(${fmt})` : fmt;
}
